import {
  useQuery,
  type UseQueryResult,
  type UseQueryOptions,
} from '@tanstack/react-query';
import { z } from 'zod';
import { useGetMeApi } from '../use-get-me-api/use-get-me-api';
import { fetcher } from '../../../../utils/api/fetcher/fetcher';
import type { ApiError } from '../../../../utils/api/ApiError/ApiError';

export const permissionsShape = z
  .object({
    manageAllUsers: z.boolean(),
    manageAllClasses: z.boolean(),
    manageAllSubscriptions: z.boolean(),
  })
  .partial();

export type Permissions = z.infer<typeof permissionsShape>;

/**
 * The permissions that together stand for the `extended_permissions` claim.
 *
 * The claim itself lives in the session token and never reaches the browser, so
 * the UI reads the permissions it implies instead: a user carrying the claim is
 * granted all of these, and a user granted all of these carries the claim.
 */
export const EXTENDED_PERMISSION_KEYS = [
  'manageAllUsers',
  'manageAllClasses',
  'manageAllSubscriptions',
] as const satisfies readonly (keyof Permissions)[];

/**
 * Whether a permissions payload amounts to the extended permission.
 *
 * Every key must be granted. A user holding only some of them has been given
 * those specific abilities, not blanket administrator access, so a partial set
 * is deliberately not enough.
 */
export function hasExtendedPermissions(permissions?: Permissions): boolean {
  if (!permissions) return false;
  return EXTENDED_PERMISSION_KEYS.every((key) => permissions[key] === true);
}

export async function getPermissionsApi(): Promise<Permissions> {
  const permissions = await fetcher<Permissions>(
    '/user-permissions/mypermissions',
    {
      service: '/permissions',
      version: '/v2',
    }
  );

  return permissionsShape.parse(permissions);
}

export const getPermissionsApiQueryKey = () =>
  ['/v2', 'user-permissions', 'mypermissions'].filter(Boolean);

export function useGetPermissionsApi(
  customQueryOptions?: Omit<
    UseQueryOptions<Permissions, ApiError>,
    'queryKey' | 'queryFn'
  >
): UseQueryResult<Permissions, ApiError> {
  return useQuery({
    queryKey: getPermissionsApiQueryKey(),
    queryFn: () => getPermissionsApi(),
    ...customQueryOptions,
  });
}

// ── Administrator access ──────────────────────────────────────────────────────

export const ADMINISTRATOR_ROLE = 'administrator';

export interface HasAdminAccess {
  /** Whether this user is an administrator by role. */
  isAdministrator: boolean;
  /**
   * Whether this user holds the extended permission without the role.
   *
   * Reported separately from `isAdministrator` because callers may treat the
   * two differently: someone who is only an admin by permission still has a
   * teacher's classes to fall back on, and an administrator does not.
   */
  hasExtendedPermission: boolean;
  /** Either of the above — admin as far as the services are concerned. */
  hasAdminAccess: boolean;
  /** True until the answer is settled — no branch should render yet. */
  isLoading: boolean;
}

/**
 * Whether a user gets the admin experience, given their access and the flag.
 *
 * An administrator by role has no teacher experience to fall back to, so the
 * flag does not decide whether they are on the admin branch — only what that
 * branch shows. An admin by permission alone keeps the full teacher experience
 * until the flag is on.
 *
 * Route loaders and React read this same function: when they disagree, a loader
 * sends an administrator down the teacher path, where they hold no personal
 * entitlements and the product guard answers 404.
 */
export function showsAdminExperience({
  isAdministrator,
  hasExtendedPermission,
  showAdminReportFlag,
}: {
  isAdministrator: boolean;
  hasExtendedPermission: boolean;
  showAdminReportFlag: boolean;
}): boolean {
  return isAdministrator || (hasExtendedPermission && showAdminReportFlag);
}

/**
 * Whether the signed-in user gets the administrator experience.
 *
 * The administrator role settles it on its own. Anyone else may still qualify
 * through the extended permission, which is the FE half of the rule the
 * services authorize on (`validateUserIsAdmin` in the reporting service: the
 * administrator role *or* the extended_permissions claim).
 *
 * The permissions call is only made for non-administrators. For an
 * administrator the answer is already known, so asking would be a request whose
 * result could not change the outcome.
 */
export function useHasAdminAccess(): HasAdminAccess {
  const { data: user, isLoading: isUserLoading } = useGetMeApi();

  const isAdministrator = user?.currentRole === ADMINISTRATOR_ROLE;
  const needsPermissionCheck = !!user && !isAdministrator;

  const { data: permissions, isLoading: isPermissionsLoading } =
    useGetPermissionsApi({ enabled: needsPermissionCheck });

  const hasExtendedPermission =
    needsPermissionCheck && hasExtendedPermissions(permissions);

  return {
    isAdministrator,
    hasExtendedPermission,
    hasAdminAccess: isAdministrator || hasExtendedPermission,
    isLoading: isUserLoading || (needsPermissionCheck && isPermissionsLoading),
  };
}
